import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/Dialog';
import { RESULT_CODES } from '@/components/payment/adyen/constants';
import { useSubscriptionInfo } from '@/hooks/user';
import ChangePlanIcon from '@/icons/change-plan.svg';
import ReactivatePlanIcon from '@/icons/reactivate-plan.svg';
import subscriptionModel from '@/models/subscription';
import { SelectPlanContent } from '@/pages/Checkout/Plan/SelectPlanModal';
import { usePlan } from '@/pages/Checkout/hooks';
import { subscribe } from '@/store/billing/thunks';
import { setPlansModalOpen, setSelectedPlan } from '@/store/checkout';
import { fetchBillingInfo } from '@/store/profile/thunks';
import { showError, showSuccess } from '@/utils';

import { useHandleRetry } from '../../hooks';

function ChangePlan({ open, confirmButtonText, onOpenChange = () => {} }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const { mainPlan = null } = usePlan();

	const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);

	const { selectedPlan } = useSelector(state => state.checkout);
	const { handlerPriceId, cancellationRequested, canUseTrial, isCanceled } =
		useSubscriptionInfo();

	const handleRetry = useHandleRetry();

	useEffect(() => {
		dispatch(setSelectedPlan(handlerPriceId));
	}, []);

	const [changingPlan, setChangingPlan] = useState(false);

	async function reactivatePlan() {
		try {
			setChangingPlan(true);
			await subscriptionModel.cancelRollback();

			await dispatch(fetchBillingInfo()).unwrap();
			showSuccess(t('checkout.toast.changePlanSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setChangingPlan(false);
			onOpenChange(false);
			setChangePlanModalOpen(false);
		}
	}

	async function switchPlan({ priceId, mainPriceId }) {
		try {
			setChangingPlan(true);

			const { handlerData } = await dispatch(
				subscribe({
					priceId,
					mainPriceId
				})
			).unwrap();

			if (handlerData?.resultCode !== RESULT_CODES.AUTHORIZED) {
				handleRetry({
					priceId
				});
			}
		} catch (error) {
			showError(error);
		} finally {
			setChangingPlan(false);
			setChangePlanModalOpen(false);
		}
	}

	const changePlan = async () => {
		const mustSwitchPlan = selectedPlan !== handlerPriceId;

		if (cancellationRequested && !isCanceled) {
			await reactivatePlan();
		} else if (cancellationRequested && isCanceled && !mustSwitchPlan) {
			handleRetry({
				priceId: handlerPriceId
			});
		} else if (mustSwitchPlan) {
			await switchPlan({
				priceId: selectedPlan,
				mainPriceId: mainPlan?.handlerId
			});
		}

		dispatch(setPlansModalOpen(false));
		setChangePlanModalOpen(false);
	};

	const onChangePlan = () => {
		dispatch(setPlansModalOpen(false));
		setChangePlanModalOpen(true);
	};

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="w-[320px] p-0 md:w-[606px]">
					<SelectPlanContent
						onOpenChange={onOpenChange}
						confirmButtonText={confirmButtonText}
						onChangePlan={() => onChangePlan()}
						blockedOptions={
							cancellationRequested && !isCanceled ? ['all'] : []
						}
						filterTrial={!canUseTrial}
					/>
				</DialogContent>
			</Dialog>

			<Dialog
				open={changePlanModalOpen}
				onOpenChange={setChangePlanModalOpen}
			>
				<DialogContent className="w-[280px] md:w-[397px]">
					<DialogTop
						title={t('billing.modals.changePlan.title')}
						description={t('billing.modals.changePlan.title')}
					/>

					<div className="flex flex-col items-center gap-2 px-4 py-9">
						{cancellationRequested || isCanceled ? (
							<ReactivatePlanIcon />
						) : (
							<ChangePlanIcon />
						)}

						<p className="whitespace-pre-line text-center text-sm text-grey-500">
							{t(
								cancellationRequested || isCanceled
									? 'billing.modals.reactivatePlan.text'
									: 'billing.modals.changePlan.text'
							)}
						</p>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								onOpenChange(false);
								setChangePlanModalOpen(false);
							}}
						>
							{t('common.close')}
						</Button>
						<Button onClick={() => changePlan()} loading={changingPlan}>
							{cancellationRequested || isCanceled
								? t('billing.modals.reactivatePlan.yesReactivate')
								: t('billing.modals.changePlan.yesChange')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

export default ChangePlan;
